/**
 * BriefWorkspace — single native React surface for brief editing and review.
 *
 * Renders:
 *   - Sticky BriefIdentityBar (64px) with persona avatar, lifecycle + health
 *     badges, and action bar.
 *   - LensTabs (Strategy | Products | Angles | Assets | Performance | Lineage).
 *     Active lens lives in the URL query param `?lens=` so it survives refresh
 *     and is shareable.
 *   - LensBody flexes to fill remaining width.
 *   - ChatSidecar always rendered on the right.  User can collapse/expand it
 *     at any time via the sidecar toggle button.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useGenerationStore } from '@/stores/generation';
import { BriefIdentityBar } from './BriefIdentityBar';
import { LensTabs } from './LensTabs';
import { ChatSidecar } from './ChatSidecar';
import { useBriefWorkspaceChat } from './useBriefWorkspaceChat';
import { StrategyLens } from './lenses/StrategyLens';
import { PerformanceLens } from './lenses/PerformanceLens';
import { ProductsLens } from './lenses/ProductsLens';
import { AnglesLens } from './lenses/AnglesLens';
import { AssetsLens } from './lenses/AssetsLens';
import { LineageLens } from './lenses/LineageLens';
import { ConnectedAdsLens } from './lenses/ConnectedAdsLens';
import { BriefRequirementsPanel } from './BriefRequirementsPanel';
import type { LensKey } from './constants';

interface BriefWorkspaceProps {
  briefId: string;
  onBack: () => void;
}

const VALID_LENSES: LensKey[] = [
  'performance',
  'connected_ads',
  'strategy',
  'products',
  'angles',
  'requirements',
  'assets',
  'lineage',
];

export function BriefWorkspace({ briefId, onBack }: BriefWorkspaceProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    selectedBrief,
    selectedBriefLoading,
    briefsError,
    fetchBrief,
    clearSelectedBrief,
  } = useGenerationStore();

  const briefAdPackJobCount = useGenerationStore(
    (s) => s.briefAdPackJobsByBriefId[briefId]?.length ?? 0,
  );

  const lensParam = searchParams.get('lens') as LensKey | null;
  // Live briefs (status === 'active') land on Performance by default so the
  // first thing users see is real spend/ROAS data, not the strategy canvas.
  const isLiveBrief = selectedBrief?.status === 'active';
  const defaultLens: LensKey = isLiveBrief ? 'performance' : 'strategy';
  const activeLens: LensKey =
    lensParam && VALID_LENSES.includes(lensParam) ? lensParam : defaultLens;
  const setActiveLens = useCallback(
    (next: LensKey) => {
      const params = new URLSearchParams(searchParams);
      params.set('lens', next);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const [recentlyPatched, setRecentlyPatched] = useState<Set<string>>(new Set());
  // Debounced refetch: when the chat agent emits a burst of patch_complete
  // events we collapse them into a single server-truth refetch so optimistic
  // merges can't drift permanently.  500ms gives the SSE stream enough time
  // to deliver consecutive patches in one burst.
  const refetchTimer = useRef<number | null>(null);

  const handlePatched = useCallback(
    (section: string, after: Record<string, unknown> | null) => {
      // 1. Optimistically merge the section into the store so the canvas
      //    reflects the new value immediately, before the refetch resolves.
      const store = useGenerationStore.getState();
      if (store.selectedBrief && store.selectedBrief.brief_id === briefId) {
        const merged = {
          ...store.selectedBrief,
          [section]: after ?? store.selectedBrief[section as keyof typeof store.selectedBrief],
        };
        useGenerationStore.setState({ selectedBrief: merged as typeof store.selectedBrief });
      }
      setRecentlyPatched((prev) => {
        const next = new Set(prev);
        next.add(section);
        return next;
      });
      window.setTimeout(() => {
        setRecentlyPatched((prev) => {
          const next = new Set(prev);
          next.delete(section);
          return next;
        });
      }, 4000);
      // 2. Schedule a debounced refetch — server is authoritative, the
      //    optimistic merge is just a perf cushion.
      if (refetchTimer.current !== null) {
        window.clearTimeout(refetchTimer.current);
      }
      refetchTimer.current = window.setTimeout(() => {
        fetchBrief(briefId);
        refetchTimer.current = null;
      }, 500);
    },
    [briefId, fetchBrief],
  );

  useEffect(() => {
    return () => {
      if (refetchTimer.current !== null) {
        window.clearTimeout(refetchTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    fetchBrief(briefId);
    return () => clearSelectedBrief();
  }, [briefId, fetchBrief, clearSelectedBrief]);

  const clientUid = selectedBrief?.client_uid ?? '';
  const chat = useBriefWorkspaceChat(briefId, clientUid, {
    onBriefPatched: handlePatched,
  });

  // The composer prefill flows from any "Draft this section" CTA in the
  // StrategyLens (Sections view) to the ChatSidecar's BriefChatPanel.  We pass it down via
  // a small piece of local state.
  const [composerPrefill, setComposerPrefill] = useState<string>('');
  const handleComposerPrefill = useCallback((text: string) => {
    setComposerPrefill(text);
    // If the user is in a lens that hides the composer (none in Phase 0
    // since the sidecar is always visible), this would auto-switch them.
  }, []);
  const clearComposerPrefill = useCallback(() => setComposerPrefill(''), []);

  // Fast-pass from Angles → Assets wizard with pre-selected angle
  const [generateAngleId, setGenerateAngleId] = useState<string | undefined>();
  const handleRequestGenerate = useCallback((angleId?: string) => {
    setGenerateAngleId(angleId);
    setActiveLens('assets');
  }, [setActiveLens]);

  // Optimistic merge when the ProductsLens saves a config change — keeps the
  // tab badge in sync without waiting for a full refetch.
  //
  // We do NOT call fetchBrief immediately here: the config change is already
  // persisted via a concurrent PATCH, and an instant GET races that PATCH,
  // returning stale data and resetting the ProductsLens config via its
  // useEffect. Instead we update the store optimistically and let the next
  // user-driven or periodic refetch reconcile server truth.
  const handleProductConfigChange = useCallback(
    (productConfig: import('@/services/api').ProductConfiguration) => {
      useGenerationStore.setState((s) => ({
        selectedBrief: s.selectedBrief
          ? { ...s.selectedBrief, product_config: productConfig }
          : s.selectedBrief,
      }));
    },
    [],
  );

  // Immediately patch selectedBrief in the store so tab badges and tab-switch
  // state are both in sync, then schedule a background refetch for
  // server-authoritative truth.
  const handleBriefMutated = useCallback(() => {
    fetchBrief(briefId);
  }, [briefId, fetchBrief]);

  // When StrategyLens completes a synthesis, optimistically update the store
  // so the icon and cached synthesis survive tab switches without a refetch.
  const handleSynthesized = useCallback(
    (updated: import('@/services/api').GenerationBrief) => {
      useGenerationStore.setState((s) => ({
        selectedBrief: s.selectedBrief
          ? {
              ...s.selectedBrief,
              synthesized_strategy: updated.synthesized_strategy,
            }
          : s.selectedBrief,
      }));
    },
    [],
  );

  const handleAnglesChanged = useCallback(
    (angles: import('@/services/api').BriefAngle[]) => {
      useGenerationStore.setState((s) => ({
        selectedBrief: s.selectedBrief
          ? { ...s.selectedBrief, selected_angles: angles }
          : s.selectedBrief,
      }));
      // Background refetch keeps store authoritative.
      fetchBrief(briefId);
    },
    [briefId, fetchBrief],
  );

  const CHAT_INTRO_KEY = 'neurograph:brief-chat-intro-seen';
  const hasSeenIntro = localStorage.getItem(CHAT_INTRO_KEY) === 'true';

  const [sidecarCollapsed, setSidecarCollapsed] = useState(hasSeenIntro);
  const [showChatIntro, setShowChatIntro] = useState(!hasSeenIntro);

  const handleSidecarExpand = useCallback(() => setSidecarCollapsed(false), []);
  const handleSidecarCollapse = useCallback(() => setSidecarCollapsed(true), []);
  const handleChatIntroDismiss = useCallback(() => {
    localStorage.setItem(CHAT_INTRO_KEY, 'true');
    setShowChatIntro(false);
    setSidecarCollapsed(true);
  }, []);
  const handleSidecarAction = useCallback(
    (prompt: string) => {
      setSidecarCollapsed(false);
      chat.sendMessage(prompt);
    },
    [chat],
  );

  if (selectedBriefLoading && !selectedBrief) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-20 text-slate-500">
        <Loader2 size={20} className="animate-spin text-teal-500" />
        <p className="text-sm">Loading brief…</p>
      </div>
    );
  }

  if (briefsError || !selectedBrief) {
    return (
      <div className="space-y-3">
        <button
          onClick={onBack}
          className="text-xs text-slate-500 hover:text-slate-800 transition"
        >
          ← Back to briefs
        </button>
        <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
          <AlertCircle size={16} />
          <span>{briefsError ?? `Brief ${briefId} not found`}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-2rem)] -mx-4 -my-4 bg-slate-50">
      <BriefIdentityBar brief={selectedBrief} onBack={onBack} onNavigateToLens={(l) => setActiveLens(l as LensKey)} />
      <LensTabs
        activeLens={activeLens}
        onChange={setActiveLens}
        brief={selectedBrief}
        briefAdPackJobCount={briefAdPackJobCount}
      />

      <div className="flex flex-1 min-h-0">
        <div className="flex-1 min-w-0 overflow-y-auto">
          {activeLens === 'strategy' && (
            <StrategyLens
              brief={selectedBrief}
              chat={chat}
              recentlyPatched={recentlyPatched}
              onComposerPrefill={handleComposerPrefill}
              onSynthesized={handleSynthesized}
              onNavigateToAngles={() => setActiveLens('angles')}
            />
          )}
          {activeLens === 'products' && (
            <ProductsLens
              brief={selectedBrief}
              onConfigChange={handleProductConfigChange}
            />
          )}
          {activeLens === 'angles' && (
            <AnglesLens
              brief={selectedBrief}
              chat={chat}
              onRequestGenerate={handleRequestGenerate}
              onAnglesChanged={handleAnglesChanged}
              onNavigateToAssets={() => setActiveLens('assets')}
            />
          )}
          {activeLens === 'requirements' && (
            <BriefRequirementsPanel
              briefId={selectedBrief.brief_id}
              onResolved={() => fetchBrief(selectedBrief.brief_id)}
              onBriefUpdated={handleBriefMutated}
            />
          )}
          {activeLens === 'requirements' && (
            <BriefRequirementsPanel
              briefId={selectedBrief.brief_id}
              onResolved={() => fetchBrief(selectedBrief.brief_id)}
              onBriefUpdated={handleBriefMutated}
            />
          )}
          {activeLens === 'assets' && (
            <AssetsLens
              brief={selectedBrief}
              initialAngleId={generateAngleId}
              onWizardClose={() => setGenerateAngleId(undefined)}
              onMutated={handleBriefMutated}
            />
          )}
          {activeLens === 'performance' && (
            <PerformanceLens
              brief={selectedBrief}
              onSwitchLens={setActiveLens}
            />
          )}
          {activeLens === 'connected_ads' && (
            <ConnectedAdsLens brief={selectedBrief} />
          )}
          {activeLens === 'lineage' && <LineageLens brief={selectedBrief} />}
        </div>

        <ChatSidecar
          brief={selectedBrief}
          chat={chat}
          collapsed={sidecarCollapsed}
          activeLens={activeLens}
          onExpand={handleSidecarExpand}
          onCollapse={handleSidecarCollapse}
          onActionClick={handleSidecarAction}
          composerPrefill={composerPrefill}
          onPrefillConsumed={clearComposerPrefill}
          showIntroCallout={showChatIntro}
          onIntroCalloutDismiss={handleChatIntroDismiss}
        />
      </div>
    </div>
  );
}
